import { useMemo } from 'react';
import { DateTime as DT } from 'luxon';
import { UserNameItem } from 'modules/user';
import { format$Price, formatPrice } from 'utils/helpers/functions/formatPrice';
import { withLocalCurrency } from 'utils/helpers/currency/currencyHelpers';
import { useSettings } from 'context/SettingsContext';
import EOverlay from 'components/others/EOverlay';
import { escapeHtml } from 'utils/helpers/functions/escapeHtml';
import SolarIcon from 'components/icons/SolarIcon';

const formatPaymentAmount = (amount: number, isUahMode: boolean) => {
    if (isUahMode) {
        return amount >= 0
            ? withLocalCurrency(formatPrice(amount))
            : withLocalCurrency(formatPrice(-amount), true);
    }
    return format$Price(amount);
};

interface Props {
    payment: Record<string, any>;
}

interface AttachLink {
    href: string;
    label: string;
}

const normalizeCloudFiles = (raw: any): AttachLink[] => {
    if (!raw) return [];
    const parsed =
        typeof raw === 'string'
            ? (() => {
                  try {
                      return JSON.parse(raw);
                  } catch {
                      return [];
                  }
              })()
            : raw;
    if (!Array.isArray(parsed)) return [];
    return parsed
        .map((item) => ({
            href: String(item?.publicUrl || item?.public_url || ''),
            label: String(item?.fileName || item?.file_name || ''),
        }))
        .filter((item) => item.href && item.label);
};

const fileLabelFromUrl = (fileName: string) => {
    try {
        const url = new URL(fileName);
        return decodeURIComponent(url.pathname.split('/').pop() || fileName);
    } catch {
        return fileName.split('/').pop() || fileName;
    }
};

const PaymentInfo = ({ payment }: Props) => {
    const { localSettings } = useSettings();
    const isUahMode = (Number(localSettings?.RATE?.current ?? 1) || 1) === 1;
    const attachments = useMemo(
        () =>
            String(payment.paymentAttach ?? payment.payment_attach ?? '')
                .split(';')
                .map((item) => item.trim())
                .filter(Boolean)
                .map((href) => ({ href, label: fileLabelFromUrl(href) })),
        [payment.paymentAttach, payment.payment_attach],
    );
    const cloudAttachments = useMemo(
        () => normalizeCloudFiles(payment?.paymentAttachCloud ?? payment?.payment_attach_cloud),
        [payment?.paymentAttachCloud, payment?.payment_attach_cloud],
    );
    const files = useMemo(() => [...attachments, ...cloudAttachments], [attachments, cloudAttachments]);
    const description = String(payment.paymentDescription ?? payment.payment_description ?? '').trim();

    return (
        <tr>
            <td className="date">
                <span className="d-block">{DT.fromMillis(payment.dt).toLocaleString(DT.DATE_MED)}</span>
                <span className="text-muted fs-12">{DT.fromMillis(payment.dt).toLocaleString(DT.TIME_SIMPLE)}</span>
            </td>
            <td className="user">
                <UserNameItem
                    user={{
                        userName: payment.userName,
                        userEmail: payment.userEmail,
                        userPicture: payment.userPicture,
                    }}
                />
            </td>
            <td className={`amount ${payment.payment > 0 ? 'text-success' : 'text-danger'}`}>
                <span className="fw-bold">{formatPaymentAmount(payment.payment, isUahMode)}</span>
            </td>
            <td className="description">
                {description ? (
                    <EOverlay text={`<div>${escapeHtml(description)}</div>`} />
                ) : (
                    <span className="text-muted">-</span>
                )}
            </td>
            <td className="attach">
                {files.length > 0 ? (
                    <div className="off-payments-attach-list">
                        {files.map((file) => (
                            <a
                                key={`${file.href}-${file.label}`}
                                href={file.href}
                                target="_blank"
                                rel="noreferrer"
                                className="off-payments-attach-link text-truncate"
                                title={file.label}
                            >
                                <SolarIcon name="paperclip" className="me-1" />
                                {file.label}
                            </a>
                        ))}
                    </div>
                ) : (
                    <span className="text-muted">-</span>
                )}
            </td>
        </tr>
    );
};

export default PaymentInfo;
